import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import type { AlertSeverity } from '@/shared/types/domain.types';

interface UsageCardProps {
  usagePercentage: number;
  severity: AlertSeverity;
  locationName?: string;
  onClick: () => void;
  showLocationName?: boolean;
}

/**
 * Get badge variant for usage severity
 */
function getBadgeVariant(severity: AlertSeverity): 'success' | 'warning' | 'danger' | 'info' {
  switch (severity) {
    case 'red':
      return 'danger';
    case 'yellow':
      return 'warning';
    case 'green':
      return 'success';
    default:
      return 'info';
  }
}

/**
 * Get color classes for usage percentage display
 */
function getColorClasses(severity: AlertSeverity): string {
  switch (severity) {
    case 'red':
      return 'text-red-700';
    case 'yellow':
      return 'text-yellow-700';
    case 'green':
      return 'text-green-700';
    default:
      return 'text-gray-700';
  }
}

export function UsageCard({
  usagePercentage,
  severity,
  locationName,
  onClick,
  showLocationName = false,
}: UsageCardProps) {
  const badgeVariant = getBadgeVariant(severity);
  const colorClasses = getColorClasses(severity);

  return (
    <Card
      padding="md"
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <span className="text-2xl" role="img" aria-label="chart-icon">
            📊
          </span>
          <div className="flex-1">
            {showLocationName && locationName && (
              <p className="text-sm font-medium text-gray-500 mb-1">{locationName}</p>
            )}
            <p className="text-sm font-medium text-gray-700 mb-2">System Usage</p>
            <div className="flex items-baseline space-x-2">
              <span className={`text-3xl font-bold ${colorClasses}`}>
                {Math.round(usagePercentage)}%
              </span>
              <span className="text-sm text-gray-600">last 30 working days</span>
            </div>
            {severity !== 'green' && (
              <p className="text-sm text-gray-600 mt-2">Low entry activity — frames have not been entered regularly</p>
            )}
          </div>
        </div>
        {severity !== 'green' && (
          <Badge variant={badgeVariant} size="sm">
            {severity === 'red' ? 'Low' : 'Below Target'}
          </Badge>
        )}
      </div>
    </Card>
  );
}
